import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const initialValues = {
  name: "",
  mobile: "",
  email: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (values) => {
  const errors = {};
  const name = values.name.trim();
  const mobileDigits = values.mobile.replace(/\D/g, "");
  const email = values.email.trim();

  if (name.length < 2) {
    errors.name = "Please enter your name.";
  }

  if (mobileDigits.length < 10 || mobileDigits.length > 15) {
    errors.mobile = "Please enter a valid mobile number.";
  }

  if (!emailPattern.test(email)) {
    errors.email = "Please enter a valid email address.";
  }

  return errors;
};

export const HealthCheckModal = ({ isOpen, onClose }) => {
  const titleId = useId();
  const nameId = useId();
  const mobileId = useId();
  const emailId = useId();
  const dialogRef = useRef(null);
  const nameRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      nameRef.current?.focus();
    }, 30);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) {
        return;
      }

      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setValues(initialValues);
      setErrors({});
      setSubmitted(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstInvalid = Object.keys(nextErrors)[0];
      dialogRef.current?.querySelector(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    setSubmitted(true);
  };

  return createPortal(
    <div className="health-check-modal" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="health-check-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="health-check-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>

        {submitted ? (
          <div className="health-check-modal__success">
            <span className="health-check-modal__success-icon" aria-hidden="true">
              <i className="fas fa-check" />
            </span>
            <h3 id={titleId}>Request received</h3>
            <p>
              Thanks, {values.name.trim().split(" ")[0]}. We will contact you
              within one business day to book your free health check.
            </p>
            <button type="button" className="health-check-modal__submit" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="health-check-modal__eyebrow">Free consultation</p>
            <h3 id={titleId} className="health-check-modal__title">
              Book a Free Health Check
            </h3>
            <p className="health-check-modal__text">
              Share your details and we will call you within one business day.
            </p>

            <form className="health-check-modal__form" onSubmit={handleSubmit} noValidate>
              <div className="health-check-modal__field">
                <label htmlFor={nameId}>Name</label>
                <input
                  ref={nameRef}
                  id={nameId}
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  value={values.name}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? `${nameId}-error` : undefined}
                />
                {errors.name ? (
                  <span id={`${nameId}-error`} className="health-check-modal__error">
                    {errors.name}
                  </span>
                ) : null}
              </div>

              <div className="health-check-modal__field">
                <label htmlFor={mobileId}>Mobile</label>
                <input
                  id={mobileId}
                  name="mobile"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Mobile number"
                  value={values.mobile}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.mobile)}
                  aria-describedby={errors.mobile ? `${mobileId}-error` : undefined}
                />
                {errors.mobile ? (
                  <span id={`${mobileId}-error`} className="health-check-modal__error">
                    {errors.mobile}
                  </span>
                ) : null}
              </div>

              <div className="health-check-modal__field">
                <label htmlFor={emailId}>Email</label>
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  value={values.email}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? `${emailId}-error` : undefined}
                />
                {errors.email ? (
                  <span id={`${emailId}-error`} className="health-check-modal__error">
                    {errors.email}
                  </span>
                ) : null}
              </div>

              <button type="submit" className="health-check-modal__submit">
                Submit
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
